import { defineStore } from "pinia";
import { computed, ref, toRefs, watch } from "vue";
import { FilterMenuType, } from "./home-filter-type";
// MARK: LocalStorage key
const LOCAL_STORAGE_KEY = "billson_market_filters";
const LOCAL_STORAGE_TIMESTAMP_KEY = "billson_market_filters_timestamp";
// MARK: Check if stored filters are expired (4 hours)
const areFiltersExpired = (timestamp) => {
    if (!timestamp)
        return false;
    try {
        const storedTime = parseInt(timestamp, 10);
        const currentTime = Date.now();
        const fourHoursInMs = 4 * 60 * 60 * 1000;
        return currentTime - storedTime > fourHoursInMs;
    }
    catch {
        return false;
    }
};
export const useHomeFilterStore = defineStore("homeFilterStore", () => {
    const initialState = () => ({
        showFilterMenu: false,
        isCallingApi: false,
        currentState: FilterMenuType.FILTER,
        sortBy: "",
        market: {
            list: [],
            pagination: {
                current_page: 1,
                total: 0,
                has_more_pages: true,
            },
        },
        minPrice: 0,
        maxPrice: 0,
        minYear: "",
        maxYear: "",
        minSizeDiameter: 0,
        maxSizeDiameter: 0,
        keywords: "",
    });
    const state = ref(initialState());
    const { keywords, showFilterMenu, isCallingApi, currentState, sortBy, market, minPrice, maxPrice, minYear, maxYear, minSizeDiameter, maxSizeDiameter, } = toRefs(state.value);
    // Store brand selection state separately
    const brandSelections = ref({});
    const conditionSelections = ref({});
    // MARK: LocalStorage - Load filters on init
    const loadFiltersFromStorage = () => {
        if (typeof window === "undefined")
            return null;
        try {
            // Check if filters are expired
            const timestamp = localStorage.getItem(LOCAL_STORAGE_TIMESTAMP_KEY);
            if (areFiltersExpired(timestamp)) {
                // Clear expired filters
                localStorage.removeItem(LOCAL_STORAGE_KEY);
                localStorage.removeItem(LOCAL_STORAGE_TIMESTAMP_KEY);
                return null;
            }
            const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (stored) {
                return JSON.parse(stored);
            }
        }
        catch (error) {
            console.error("Error loading filters from localStorage:", error);
        }
        return null;
    };
    const saveFiltersToStorage = () => {
        if (typeof window === "undefined")
            return;
        try {
            const filtersToStore = {
                sortBy: sortBy.value,
                minPrice: minPrice.value,
                maxPrice: maxPrice.value,
                minYear: minYear.value,
                maxYear: maxYear.value,
                minSizeDiameter: minSizeDiameter.value,
                maxSizeDiameter: maxSizeDiameter.value,
                keywords: keywords.value,
                brandSelections: brandSelections.value,
                conditionSelections: conditionSelections.value,
            };
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(filtersToStore));
            // Save timestamp
            localStorage.setItem(LOCAL_STORAGE_TIMESTAMP_KEY, Date.now().toString());
        }
        catch (error) {
            console.error("Error saving filters to localStorage:", error);
        }
    };
    // MARK: Initialize state from localStorage if available
    const storedFilters = loadFiltersFromStorage();
    if (storedFilters) {
        state.value.sortBy = storedFilters.sortBy;
        state.value.minPrice = storedFilters.minPrice;
        state.value.maxPrice = storedFilters.maxPrice;
        state.value.minYear = storedFilters.minYear;
        state.value.maxYear = storedFilters.maxYear;
        if (typeof storedFilters.minSizeDiameter === "number" &&
            typeof storedFilters.maxSizeDiameter === "number" &&
            storedFilters.minSizeDiameter === 18 &&
            storedFilters.maxSizeDiameter === 55) {
            // Previous “full slider span” meant no filter; map to price-style unset (0).
            state.value.minSizeDiameter = 0;
            state.value.maxSizeDiameter = 0;
        }
        else {
            state.value.minSizeDiameter =
                typeof storedFilters.minSizeDiameter === "number"
                    ? storedFilters.minSizeDiameter
                    : 0;
            state.value.maxSizeDiameter =
                typeof storedFilters.maxSizeDiameter === "number"
                    ? storedFilters.maxSizeDiameter
                    : 0;
        }
        state.value.keywords = storedFilters.keywords;
        brandSelections.value = storedFilters.brandSelections;
        conditionSelections.value = storedFilters.conditionSelections;
    }
    // MARK: Watch for changes and save to localStorage
    watch([
        sortBy,
        minPrice,
        maxPrice,
        minYear,
        maxYear,
        minSizeDiameter,
        maxSizeDiameter,
        keywords,
        brandSelections,
        conditionSelections,
    ], () => {
        saveFiltersToStorage();
    }, { deep: true });
    const changeMenu = (state) => {
        currentState.value = state;
    };
    const SortOption = [
        { label: "Latest", value: "" },
        { label: "Verified Genuine", value: "verified" },
        { label: "Price: Low to High", value: "price-low" },
        { label: "Price: High to Low", value: "price-high" },
    ];
    const totalFilterCount = computed(() => {
        let count = 0;
        // Count brands: individual brands if < 4, or 1 if >= 4
        if (selectedBrands.value.length > 0) {
            count +=
                selectedBrands.value.length < 4 ? selectedBrands.value.length : 1;
        }
        // Count conditions: individual conditions if < 4, or 1 if >= 4
        if (selectedConditions.value.length > 0) {
            count +=
                selectedConditions.value.length < 4
                    ? selectedConditions.value.length
                    : 1;
        }
        // Count price filter: 1 if active
        if (minPrice.value > 0 || maxPrice.value > 0) {
            count += 1;
        }
        // Count year filter: 1 if active
        if (minYear.value || maxYear.value) {
            count += 1;
        }
        // Count case size filter: 1 if active (same idea as price)
        if (minSizeDiameter.value > 0 || maxSizeDiameter.value > 0) {
            count += 1;
        }
        return count;
    });
    // Computed lists of available filters (brands/conditions) and the currently selected items
    const brands = computed(() => {
        if (!window.serverData)
            return [];
        return window.serverData.brands
            .map((item) => {
            return {
                ...item,
                isSelected: brandSelections.value[item.id] || false,
            };
        })
            .sort((a, b) => a.name.localeCompare(b.name));
    });
    const conditions = computed(() => {
        if (!window.serverData)
            return [];
        return window.serverData.conditions.map((item) => {
            return {
                ...item,
                isSelected: conditionSelections.value[item.id] || false,
            };
        });
    });
    // Methods to get selected items
    const selectedBrands = computed(() => {
        return brands.value.filter((item) => item.isSelected);
    });
    const selectedConditions = computed(() => {
        return conditions.value.filter((item) => item.isSelected);
    });
    // Market listing parameters computed from the current filter state
    const marketListingParam = computed(() => {
        return {
            tab: "Market",
            sort: sortBy.value, // "", verified, "price-low", "price-high"
            keywords: keywords.value,
            brands: selectedBrands.value.map((item) => item.name), // brand name
            conditions: selectedConditions.value.map((item) => item.name), // New, New old stock, Pre-owned
            min_price: minPrice.value === 0 ? "" : minPrice.value.toString(),
            max_price: maxPrice.value === 0 ? "" : maxPrice.value.toString(),
            min_year: minYear.value,
            max_year: maxYear.value,
            min_size_diameter: minSizeDiameter.value === 0 ? "" : minSizeDiameter.value.toString(),
            max_size_diameter: maxSizeDiameter.value === 0 ? "" : maxSizeDiameter.value.toString(),
            page: market.value.pagination.current_page,
        };
    });
    const getMarketListingApi = async () => {
        isCallingApi.value = true;
        const xsrfToken = decodeURIComponent(document.cookie
            .split("; ")
            .find((cookie) => cookie.startsWith("XSRF-TOKEN="))
            ?.split("=")[1] || "");
        return await fetch("/api/v1/listings", {
            method: "POST",
            credentials: "include",
            body: JSON.stringify(marketListingParam.value),
            headers: {
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest",
                "Content-Type": "application/json",
                "X-XSRF-TOKEN": xsrfToken,
            },
        })
            .then((response) => response.json())
            .then((data) => {
            if (data.result.pagination.current_page == 1) {
                market.value.list = data.result.list;
                market.value.pagination = data.result.pagination;
            }
            else {
                market.value.list.push(...(data.result.list || []));
                market.value.pagination = data.result.pagination;
            }
            isCallingApi.value = false;
            return data;
        })
            .catch((error) => {
            console.error("Error submitting sort:", error);
            isCallingApi.value = false;
            return null;
        });
        return null;
    };
    // Simple debounce utility to avoid spamming the API
    const debounce = (fn, wait = 300) => {
        let timeout = null;
        return (...args) => {
            if (timeout)
                clearTimeout(timeout);
            timeout = setTimeout(() => fn(...args), wait);
        };
    };
    // Debounced wrapper around the API call
    const debouncedGetMarketListingApi = debounce(() => {
        // fire-and-forget; callers can still call getMarketListingApi directly if they need the result
        getMarketListingApi();
    }, 350);
    // Watch for changes to marketListingParam but ignore changes that only affect `page`.
    // When other params change, reset page to 1 and trigger a debounced API call.
    watch(marketListingParam, (newVal, oldVal) => {
        if (!oldVal)
            return;
        // Compare everything except `page`. Handle initial run when oldVal is undefined.
        const { page: _newPage, ...newRest } = newVal;
        const { page: _oldPage, ...oldRest } = oldVal || {};
        const changed = JSON.stringify(newRest) !== JSON.stringify(oldRest);
        if (changed) {
            // reset to page 1 (most APIs expect 1-based paging; keep behaviour consistent with request)
            if (market.value && market.value.pagination) {
                market.value.pagination.current_page = 1;
            }
            debouncedGetMarketListingApi();
        }
    }, { deep: true });
    const title = computed(() => {
        return (currentState.value.charAt(0).toUpperCase() + currentState.value.slice(1));
    });
    // Methods to update selections
    const toggleBrandSelection = (brandId) => {
        brandSelections.value[brandId] = !brandSelections.value[brandId];
    };
    const toggleConditionSelection = (conditionId) => {
        conditionSelections.value[conditionId] =
            !conditionSelections.value[conditionId];
    };
    const prices = [];
    const years = [];
    const others = [];
    // MARK: Reset
    const reset = () => {
        // Clear localStorage
        if (typeof window !== "undefined") {
            try {
                localStorage.removeItem(LOCAL_STORAGE_KEY);
                localStorage.removeItem(LOCAL_STORAGE_TIMESTAMP_KEY);
            }
            catch (error) {
                console.error("Error clearing filters from localStorage:", error);
            }
        }
        // Reset brand and condition selections
        brandSelections.value = {};
        conditionSelections.value = {};
        // Reset state
        return Object.assign(state.value, initialState());
    };
    // MARK: Clear filters from localStorage (exported function)
    const clearStoredFilters = () => {
        if (typeof window !== "undefined") {
            try {
                localStorage.removeItem(LOCAL_STORAGE_KEY);
                localStorage.removeItem(LOCAL_STORAGE_TIMESTAMP_KEY);
            }
            catch (error) {
                console.error("Error clearing filters from localStorage:", error);
            }
        }
    };
    // MARK: Return
    return {
        isCallingApi,
        sortBy,
        market,
        totalFilterCount,
        getMarketListingApi,
        SortOption,
        showFilterMenu,
        currentState,
        changeMenu,
        title,
        brands,
        conditions,
        prices,
        years,
        others,
        selectedBrands,
        selectedConditions,
        toggleBrandSelection,
        toggleConditionSelection,
        minPrice,
        maxPrice,
        minYear,
        maxYear,
        minSizeDiameter,
        maxSizeDiameter,
        keywords,
        reset,
        clearStoredFilters,
    };
});
